import { ThemeProvider } from "@mui/material/styles";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { BrowserRouter as Router, useLocation } from "react-router-dom";

import { IndexRouter } from "./setup/routes/routes";
import { ScrollToTop } from "./components/common/scrollToTop";
import { setAppToken } from "./setup/context/api";
import theme from "./styles/common/theme/theme";
import { useCallback, useEffect, useState } from "react";
import { AuthConfig } from "./setup/context/config/authConfig";
import Sidebar from "./pages/business/sidebar";
import { isLoggedIn } from "./setup/context/authContext";
import "./App.css";
import AlertSystem from "./components/common/AlertSystem/AlertSystem";
import { ROUTES } from "./setup/constants";

// Create rtl cache
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

const hideSidebarRoutes = [
  ROUTES.CREATE_COMPLEX_CONTRACT.replace(":milestoneId?", "").replace(
    ":complexId?/",
    ""
  ),
  ROUTES.CONTRACT,
  ROUTES.SETTLEMENTS,
];
const whiteBackgroundRoutes = [
  ROUTES.CREATE_COMPLEX_CONTRACT.replace(":milestoneId?", "").replace(
    ":complexId?/",
    ""
  ),
  ROUTES.CONTRACT,
  ROUTES.SETTLEMENTS,
];

export const App = () => {
  const [isTokenReady, setIsTokenReady] = useState(false);

  // Wait for token to be available
  useEffect(() => {
    const checkToken = () => {
      const userToken = AuthConfig.getToken();
      if (userToken) {
        setIsTokenReady(true);
      } else {
        setIsTokenReady(true);
      }
    };

    // Check immediately
    checkToken();

    // Also check after a short delay in case token is being set asynchronously
    const timeoutId = setTimeout(checkToken, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  function AppContent() {
    // Don't render anything until token is ready to prevent wrong page display
    if (!isTokenReady) {
      return (
        <CacheProvider value={cacheRtl}>
          <ThemeProvider theme={theme}>
            <div dir="rtl">
              <ScrollToTop path="/">
                {/* Loading state while determining role */}
                <div />
              </ScrollToTop>
              <AlertSystem />
            </div>
          </ThemeProvider>
        </CacheProvider>
      );
    }

    function SidebarContainer() {
      const location = useLocation();
      const isHideSidebar = hideSidebarRoutes.some((route) =>
        location?.pathname?.includes(route)
      );
      if (isHideSidebar) return null;
      return isLoggedIn() ? <Sidebar /> : null;
    }

    function MainContainer() {
      const location = useLocation();
      const isWhiteBackground = whiteBackgroundRoutes.some((route) =>
        location?.pathname?.includes(route)
      );
      const clientAdminStyle = {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        background: isWhiteBackground ? "white" : "",
      };

      return (
        <div style={!isLoggedIn() ? {} : { ...clientAdminStyle }}>
          <SidebarContainer />
          <div className={!isLoggedIn() ? undefined : "outletContainer"}>
            <IndexRouter />
          </div>
        </div>
      );
    }

    return (
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <div dir="rtl">
            <ScrollToTop path="/">
              <Router>
                <MainContainer />
              </Router>
            </ScrollToTop>
            <AlertSystem />
          </div>
        </ThemeProvider>
      </CacheProvider>
    );
  }

  return <AppContent />;
};
setAppToken();
